"use client"

import React, { ReactElement, ReactNode } from "react"
import Link from "next/link"
import { APP_B2B_ACCOUNT_OWNER } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import clsx from "clsx"
import { EditIcon, PlusIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { hasAnyRole } from "@/lib/role-helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import { normalizeBirthday } from "@/components/account/profile/personal/normalizeBirthday"
import { UbAccountProfileViewStoryblok } from "@/components/storyblok/component-types"

interface AccountProfileProps {
  accountContext: AccountContext
  blok: UbAccountProfileViewStoryblok
}

export default function AccountProfileClient(props: AccountProfileProps) {
  const { profileUpdated, setProfileUpdated } = useUbStorefrontState()
  const t = useTranslations("storefront")

  const formattedBirthday = normalizeBirthday(props.accountContext.userData?.birthday)

  const cards: ProfileInfoCardData[] = [
    {
      title: props.blok.title_email,
      editUrl: getLinkWithPrefixFromURLAndLocale("/account/profile/email", props.accountContext.sbLocale),
      fields: [
        {
          label: props.blok.email,
          value: props.accountContext.userData?.email,
        },
      ],
    },
    {
      title: props.blok.title_password,
      editUrl: getLinkWithPrefixFromURLAndLocale("/account/profile/password", props.accountContext.sbLocale),
      fields: [
        {
          label: props.blok.password,
          value: "******",
        },
      ],
    },
    {
      title: props.blok.title_company,
      hidden:
        props.accountContext.customerData?.customerType == "PRIVATE" ||
        !hasAnyRole(props.accountContext, [APP_B2B_ACCOUNT_OWNER]),
      editUrl: getLinkWithPrefixFromURLAndLocale("/account/profile/company", props.accountContext.sbLocale),
      fields: [
        {
          label: props.blok.company_name,
          value: props.accountContext.customerData?.companyName,
        },
        {
          hidden: props.accountContext.customerData?.companyName2 == undefined,
          label: props.blok.company_name_2,
          value: props.accountContext.customerData?.companyName2,
        },
        {
          label: props.blok.industry,
          value: props.accountContext.customerData?.industry
            ? t(`account.form.line_of_business.${props.accountContext.customerData.industry}`)
            : undefined,
        },
        {
          label: props.blok.taxation_id,
          value: props.accountContext.customerData?.taxationID,
        },
        {
          label: props.blok.invoice_email,
          value:
            props.accountContext.userData?.invoiceEmail && props.accountContext.userData?.invoiceEmail.length > 0
              ? props.accountContext.userData.invoiceEmail
              : props.accountContext.userData?.email,
        },
      ],
    },
    {
      title: props.blok.title_personal,
      editUrl: getLinkWithPrefixFromURLAndLocale("/account/profile/personal", props.accountContext.sbLocale),
      fields: [
        {
          label: props.blok.name,
          value: (
            <>
              {props.accountContext.userData?.title}&nbsp;
              {props.accountContext.userData?.honorific}
              {props.accountContext.userData?.honorific && <>&nbsp;</>}
              {props.accountContext.userData?.firstName}&nbsp;{props.accountContext.userData?.lastName}
            </>
          ),
        },
        {
          hidden: props.accountContext.customerData?.customerType == "PRIVATE",
          label: props.blok.department,
          value: props.accountContext.userData?.department,
        },
        {
          label: props.blok.phone,
          value: props.accountContext.userData?.phoneBusiness,
        },
        {
          label: props.blok.fax,
          value: props.accountContext.userData?.fax,
        },
        {
          hidden: formattedBirthday.length == 0,
          label: props.blok.birthday,
          value: formattedBirthday,
        },
        {
          hidden: formattedBirthday.length > 0,
          label: props.blok.birthday,
          value: props.blok.birthday_not_set,
        },
        {
          hidden: props.accountContext.userData?.preferredLanguage == undefined,
          label: props.blok.preferred_language,
          value: props.blok["language_" + props.accountContext.userData?.preferredLanguage],
        },
        {
          label: props.blok.newsletter,
          value: props.accountContext.subscriptionActive
            ? props.blok.newsletter_active
            : props.accountContext.userData?.subscriptionPending
              ? props.blok.newsletter_pending
              : props.blok.newsletter_unsubscribed,
        },
      ],
    },
  ]

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        {profileUpdated && (
          <div
            className="
              flex cursor-pointer flex-row items-center justify-between gap-2 rounded-sm border border-gray-300
              bg-green-700 p-2 text-primary-foreground
            "
            onClick={() => setProfileUpdated(false)}
          >
            <h2 className="text-sm font-semibold md:text-base">{props.blok.profile_updated}</h2>
            <PlusIcon className={"-mr-1 inline size-8 rotate-45 md:size-6"} />
          </div>
        )}

        <div className={`grid auto-rows-auto grid-cols-2 gap-2 text-sm lg:grid-cols-4`}>
          {cards
            .filter((card) => !card.hidden)
            .map((card) => (
              <ProfileInfoCard key={card.editUrl} info={card} fieldClassName={"odd:bg-gray-50 hover:bg-gray-100"} />
            ))}
        </div>
      </div>
    </>
  )
}

export interface ProfileInfoCardData {
  title: string | ReactElement | undefined
  hidden?: boolean
  editUrl: string
  fields: {
    hidden?: boolean
    label: string | undefined
    value: string | ReactNode | undefined
  }[]
}

interface ProfileInfoCardProps extends React.HTMLProps<HTMLDivElement> {
  info: ProfileInfoCardData
  fieldClassName?: clsx.ClassValue
}

export const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({ info, className, fieldClassName, ...props }) => (
  <div
    key={info.editUrl}
    className={cn(
      "col-span-2 grid auto-rows-max grid-cols-subgrid gap-y-1 rounded-sm border border-gray-300 bg-white py-2",
      className
    )}
    {...props}
  >
    <div className={"col-span-2 mb-1 flex justify-between px-2"}>
      <h2 className="text-lg">{info.title}</h2>
      <Link href={info.editUrl} className={"mt-0.5 inline-block size-5 hover:text-primary-hover"}>
        <EditIcon className="size-5" />
      </Link>
    </div>
    {info.fields
      .filter((f) => !f.hidden)
      .map((field, i) => (
        <div
          key={(field.label ?? "") + i}
          className={cn("col-span-2 grid grid-cols-subgrid px-2 text-wrap hyphens-auto", fieldClassName)}
        >
          <span className={"font-light"}>{field.label}</span>
          <div>{field.value}</div>
        </div>
      ))}
  </div>
)
